/**
 * Document API Types
 * Types for document management, custom fields, and extraction data
 */
import type { DocumentPaginationDto } from "./common";

// =============================================================================
// ENUMS
// =============================================================================

/**
 * Status values for documents
 */
export enum DocumentStatusEnum {
    Pending = "Pending",
    Parsed = "Parsed",
    Queued = "Queued",
    Exported = "Exported",
    Rejected = "Rejected",
    Error = "Error",
    Deleted = "Deleted",
}

/**
 * Data types for custom field definitions
 */
export enum FieldTypeEnum {
    String = "String",
    Integer = "Integer",
    Decimal = "Decimal",
    Date = "Date",
    Boolean = "Boolean",
}

/**
 * Source of document creation
 */
export enum DocumentSourceEnum {
    Upload = "Upload",
    API = "API",
    Email = "Email",
    Batch = "Batch",
    Generated = "Generated",
}

/**
 * Types of relationships between documents
 * Used in document graph matching to define how documents are connected
 */
export enum RelationTypeEnum {
    Split = "Split",
    Supporting = "Supporting",
    Reference = "Reference",
    Match = "Match",
}

// =============================================================================
// DOCUMENT TYPE
// =============================================================================

/**
 * Document type information
 */
export interface DocumentTypeDto {
    id: number;
    name?: string;
    code?: string;
    /** Whether this document type is inbound (user-uploadable) or system-generated */
    isInbound: boolean;
}

// =============================================================================
// CUSTOM FIELDS
// =============================================================================

/**
 * Definition of a custom field for document extraction
 */
export interface CustomFieldDefinition {
    id: number;
    clientId: number;
    documentTypeId?: number;
    entityType: string;
    fieldCode: string;
    fieldLabel: string;
    fieldType: FieldTypeEnum;
    displayOrder: number;
    isRequired: boolean;
    isVisible: boolean;
    isSearchable: boolean;
    isExportable: boolean;
    isIndexed: boolean;
    isCalculated: boolean;
    calculationExpression?: string;
    helpText?: string;
    validationRules?: string;
}

/**
 * Extracted field value from document parsing
 * (Full entity with nested definition - used internally)
 */
export interface CustomFieldValue {
    id: number;
    clientId: number;
    customFieldDefinitionId: number;
    customFieldDefinition?: CustomFieldDefinition;
    entityType: string;
    entityId: number;
    stringValue?: string;
    decimalValue?: number;
    integerValue?: number;
    dateValue?: string;
    booleanValue?: boolean;
    createdAt: string;
    updatedAt: string;
}

/**
 * Custom field value with flattened definition properties
 * Used in DocumentDetailDto for API responses
 *
 * Important: Only ONE value property should be populated based on fieldType:
 * - fieldType "String" → stringValue
 * - fieldType "Integer" → integerValue
 * - fieldType "Decimal" → decimalValue
 * - fieldType "Date" → dateValue
 * - fieldType "Boolean" → booleanValue
 */
export interface CustomFieldValueDto {
    /** Unique identifier for this custom field value */
    id: number;
    /** Reference to the field definition */
    customFieldDefinitionId: number;
    /** Machine-readable code for the field (e.g., "invoice_date") */
    fieldCode: string;
    /** Human-readable label for display (e.g., "Invoice Date") */
    fieldLabel: string;
    /** Data type of the field */
    fieldType: string;
    /** Order in which this field should be displayed */
    displayOrder: number;
    /** String value (populated when fieldType is "String") */
    stringValue?: string;
    /** Decimal value (populated when fieldType is "Decimal") */
    decimalValue?: number;
    /** Integer value (populated when fieldType is "Integer") */
    integerValue?: number;
    /** Date value (populated when fieldType is "Date") */
    dateValue?: string;
    /** Boolean value (populated when fieldType is "Boolean") */
    booleanValue?: boolean;
    /** When true, the field is included in exports (CSV/ERP). Internal-only fields are false and must be excluded from export output. */
    isExportable: boolean;
    /** When true, the field value failed validation. Export is blocked until this is resolved. */
    containsError?: boolean;
    /**
     * When the stored value row was last written — the optimistic-concurrency baseline.
     * Kept as the raw ISO string and echoed back verbatim as baseUpdatedAt when saving
     * (never converted to Date, so the round-trip is lossless). Absent on older backends.
     */
    updatedAt?: string | null;
}

/**
 * A line item belonging to a document, with its own custom field values.
 */
export interface LineItemDto {
    /** Unique identifier for this line item */
    id: number;
    /** Identifier of the document this line item belongs to */
    documentId: number;
    /** 1-based position of this line item within the document */
    lineNumber: number;
    /** Collection of line-item-scoped custom field values with flattened field definitions */
    customFieldValues?: CustomFieldValueDto[];
}

// =============================================================================
// EMBEDDED TYPES
// =============================================================================

/**
 * Embedded file storage information in document details
 */
export interface FileStorageDto {
    id: number;
    fileName?: string;
    blobPath?: string;
    fileSizeBytes?: number;
    contentType?: string;
    /**
     * Canonical file format, camelCase-serialized enum: "pdf" | "xls" | "xlsx" | "doc" | "docx" | "unknown".
     * Null/absent when the backend predates file-type validation — fall back to contentType/extension.
     */
    fileType?: string | null;
}

/**
 * Extraction version information
 */
export interface ExtractionVersionDto {
    id: number;
    version: number;
    confidence?: number;
    createdAt?: string;
}

/**
 * Document page range information
 */
export interface DocumentPagesDto {
    start: number;
    end: number;
    total: number;
}

/**
 * Relationship between two documents created by the matching process
 */
export interface DocumentRelationDto {
    id: number;
    parentDocId: number;
    childDocId: number;
    relationType: RelationTypeEnum;
    metadata?: object;
    createdAt: string;
}

// =============================================================================
// DOCUMENT DTOs
// =============================================================================

/**
 * Lightweight document information for list views
 */
export interface DocumentListItemDto {
    id: number;
    fileName?: string;
    /** Auto-generated document name based on extracted fields (e.g., "Invoice_VendorName_2026-01-15_INV001") */
    generatedName: string;
    status: DocumentStatusEnum;
    createdAt: string;
    /** Client ID */
    clientId?: number;
    /** Client name */
    clientName?: string;
    organizationId?: number;
    organizationName?: string;
    documentType?: string;
    /** Whether this document type is inbound (user-uploadable). Null if document type is not yet assigned */
    isInbound?: boolean | null;
    /** Indicates whether the processing end state for this document is reached */
    isProcessingEndStateReached: boolean;
    /** The code of the current workflow step. Null if no workflow is active or if the processing end state has been reached */
    currentStepCode?: string | null;
    /** Optional error code from the latest exception related to this document */
    errorCode?: string;
    /** Human-readable error message from the latest exception (dev mode only) */
    errorMessage?: string;
    /** When true, error is transient and will be retried automatically. errorCode/errorMessage are always null in this state. */
    hasRetryWarning?: boolean;
    /** When true, errorCode comes from a permanently failed ExternalExport job (push failure, not a data problem) — the document remains exportable via user-initiated retry. */
    isExportFailed?: boolean;
    /** When true, all required custom field definitions for this document type have a populated value. Defaults to true when no required fields exist. */
    hasAllRequiredFields?: boolean;
    /** When true, at least one custom field value has containsError = true. Export is blocked until all field errors are resolved. */
    hasFieldErrors?: boolean;
    /** When true, the document passed the retention window and was included in a removal-reminder cycle — the next monthly retention run will delete it. */
    isRetentionExpired?: boolean;
}

/**
 * Full document details with extraction data
 * Main DTO for document retrieval and export operations
 */
export interface DocumentDetailDto {
    /** Unique identifier for the document */
    id: number;
    /** Name of the source file */
    fileName?: string;
    /** Auto-generated document name based on extracted fields (e.g., "Invoice_VendorName_2026-01-15_INV001") */
    generatedName: string;
    /** Current status of the document */
    status: DocumentStatusEnum;
    /** Source of document creation */
    documentSource: DocumentSourceEnum;
    /** Timestamp when the document was created */
    createdAt: string;
    /** Timestamp when the document was last updated */
    updatedAt: string;
    /** Organization that owns this document (nullable until parsing completes) */
    organizationId?: number;
    /** Name of the organization */
    organizationName?: string;
    /** Classification information about the document type */
    documentType?: DocumentTypeDto;
    /** Storage and file metadata information */
    fileStorage?: FileStorageDto;
    /** Information about the current data extraction version */
    extractionVersion?: ExtractionVersionDto;
    /** Collection of custom field values with flattened field definitions */
    customFieldValues?: CustomFieldValueDto[];
    /** Line items belonging to this document, each with its own custom field values */
    lineItems?: LineItemDto[];
    /** Relationships where this document is the child */
    parentRelations?: DocumentRelationDto[];
    /** Relationships where this document is the parent */
    childRelations?: DocumentRelationDto[];
    /** Pagination information for document pages */
    pages: DocumentPagesDto;
    /** Indicates whether the processing end state for this document is reached */
    isProcessingEndStateReached: boolean;
    /** The code of the current workflow step. Null if no workflow is active or if the processing end state has been reached */
    currentStepCode?: string | null;
    /** Optional error code from the latest exception related to this document */
    errorCode?: string;
    /** Human-readable error message from the latest exception (dev mode only) */
    errorMessage?: string;
    /** When true, error is transient and will be retried automatically. errorCode/errorMessage are always null in this state. */
    hasRetryWarning?: boolean;
    /** When true, errorCode comes from a permanently failed ExternalExport job (push failure, not a data problem) — the document remains exportable via user-initiated retry. */
    isExportFailed?: boolean;
}

/**
 * Lightweight document status for polling
 */
export interface DocumentStatusDto {
    id: number;
    status: DocumentStatusEnum;
    updatedAt: string;
    /** Indicates whether the processing end state for this document is reached */
    isProcessingEndStateReached: boolean;
    /** The code of the current workflow step. Null if no workflow is active or if the processing end state has been reached */
    currentStepCode?: string | null;
    /** Optional error code from the latest exception related to this document */
    errorCode?: string;
    /** Human-readable error message from the latest exception (dev mode only) */
    errorMessage?: string;
    /** When true, error is transient and will be retried automatically. errorCode/errorMessage are always null in this state. */
    hasRetryWarning?: boolean;
    /** When true, errorCode comes from a permanently failed ExternalExport job (push failure, not a data problem) — the document remains exportable via user-initiated retry. */
    isExportFailed?: boolean;
}

// =============================================================================
// REQUEST/RESPONSE DTOs
// =============================================================================

/**
 * Response for document list endpoint
 */
export interface DocumentListResponseDto {
    data: DocumentListItemDto[];
    pagination: DocumentPaginationDto;
}

/**
 * Request parameters for listing documents
 */
export interface DocumentListParams {
    status?: DocumentStatusEnum;
    organizationId?: number;
    createdAfter?: string;
    createdBefore?: string;
    limit?: number;
    offset?: number;
}

/**
 * Request body for retrieving multiple documents by IDs
 */
export interface GetDocumentsByIdsRequestDto {
    documentIds: number[];
}

/**
 * Response for retrieving multiple documents by IDs
 */
export interface GetDocumentsByIdsResponseDto {
    documents: DocumentDetailDto[];
    total: number;
}

/**
 * Response for retrieving available document types
 */
export interface GetDocumentTypesResponseDto {
    documentTypes: DocumentTypeDto[];
    total: number;
}

/**
 * Response for retrieving custom field definitions for a document type or line item type
 */
export interface GetCustomFieldDefinitionsResponseDto {
    /** Array of custom field definitions, ordered by displayOrder */
    customFieldDefinitions: CustomFieldDefinitionDto[];
    /**
     * LineItem-scoped definitions for the document type's line item types (same organization).
     * Optional: absent on older backends.
     */
    lineItemCustomFieldDefinitions?: CustomFieldDefinitionDto[];
    /** Total number of custom field definitions returned */
    total: number;
    /** The document type ID (or line item type ID) that was queried */
    entityId: number;
}

/**
 * Custom field definition DTO (from API)
 * Used for document type and line item type custom fields
 */
export interface CustomFieldDefinitionDto {
    /** Custom field definition ID */
    id: number;
    /** Client ID (null for global definitions) */
    clientId?: number | null;
    /** Entity type this field applies to (e.g., "Document", "LineItem") */
    entityType: string;
    /** Associated document type ID or line item type ID */
    entityId?: number | null;
    /** Unique field code (e.g., "invoice_number", "total_amount") */
    fieldCode: string;
    /** Human-readable label for the field */
    fieldLabel: string;
    /** Data type (String, Decimal, Integer, Date, Boolean) */
    fieldType: string;
    /** Whether the field is required */
    isRequired: boolean;
    /** Whether the field is visible in UI */
    isVisible: boolean;
    /** Whether the field is included in exports */
    isExportable: boolean;
    /** Target system field name for exports (e.g., SQL column name). When null and isExportable is true, defaults to fieldCode */
    exportFieldName?: string | null;
    /** Whether the field is searchable */
    isSearchable: boolean;
    /** Whether the field has a database index for performance */
    isIndexed: boolean;
    /** Whether the field value is calculated from an expression rather than extracted. Calculated fields are automatically computed after all non-calculated fields are saved */
    isCalculated: boolean;
    /** NCalc expression for calculating the field value (e.g., "purchase_price * quantity_accepted"). Only used when isCalculated is true. The expression can reference other field codes on the same entity */
    calculationExpression?: string | null;
    /** Whether the field value is system-generated rather than user-entered */
    isGenerated: boolean;
    /** Optional entitlement code required to access this field */
    entitlementCode?: string | null;
    /** Order for display in UI (lower numbers appear first) */
    displayOrder: number;
    /** JSON validation rules */
    validationRules?: string | null;
    /** Default value for the field */
    defaultValue?: string | null;
    /** Help text to display in UI */
    helpText?: string | null;
    /** Creation timestamp */
    createdAt: string;
    /** Last update timestamp */
    updatedAt: string;
}

/**
 * Request body for document export
 */
export interface ExportRequestDto {
    documentIds: number[];
    format?: "csv" | "external";
}

/**
 * Response for document export
 */
export interface ExportResponseDto {
    documents: DocumentDetailDto[];
    exportedCount: number;
}

/**
 * Request body for batch status update
 */
export interface UpdateStatusBatchRequestDto {
    documentIds: number[];
    status: DocumentStatusEnum;
}

/**
 * Response for batch status update
 */
export interface UpdateStatusBatchResponseDto {
    updatedCount: number;
    status: DocumentStatusEnum;
}

/**
 * Request body for batch status retrieval (polling multiple documents)
 */
export interface GetDocumentStatusBatchRequestDto {
    documentIds: number[];
}

/**
 * Response for batch status retrieval
 */
export interface GetDocumentStatusBatchResponseDto {
    statuses: DocumentStatusDto[];
    total: number;
    notFoundIds: number[];
}

/**
 * Request body for updating custom field values
 * PATCH /api/documents/{id}/customfields
 */
export interface UpdateCustomFieldsRequestDto {
    customFieldValues: UpdateCustomFieldValueDto[];
    /**
     * When true, the backend rejects the whole request with 409 BizConcurrentUpdateConflict
     * if any targeted field value was written since the client loaded it (per-field check
     * via each value's baseUpdatedAt). Omit/false preserves legacy last-write-wins behavior.
     */
    checkConcurrency?: boolean;
}

/**
 * DTO for a single custom field value update
 * If value is null, the custom field value will be deleted
 *
 * The value is provided as a string and automatically converted to the appropriate type
 * based on the custom field definition's field type.
 */
export interface UpdateCustomFieldValueDto {
    /** Identifies which custom field definition to update/add/delete */
    customFieldDefinitionId: number;
    /**
     * When set, the value targets this line item (must belong to the document in the route)
     * and the definition must be LineItem-scoped. When omitted, the value targets the document.
     */
    lineItemId?: number;
    /**
     * The value as a string. Will be converted to the appropriate type based on the
     * custom field definition's field type. If null or empty, the custom field value will be deleted.
     *
     * Format examples:
     * - String: "Invoice Number"
     * - Decimal: "1234.56"
     * - Integer: "42"
     * - Date: "2026-01-15T00:00:00Z" (ISO 8601 format)
     * - Boolean: "true", "false", "1", "0", "yes", "no" (case-insensitive)
     */
    value?: string | null;
    /**
     * Optimistic-concurrency baseline: the stored value's updatedAt exactly as loaded
     * (CustomFieldValueDto.updatedAt, echoed verbatim). Null means "no value existed when
     * I loaded the document". Only evaluated when the request sets checkConcurrency.
     */
    baseUpdatedAt?: string | null;
}

/**
 * Response for custom field values update
 */
export interface UpdateCustomFieldsResponseDto {
    /** Number of custom field values created */
    addedCount: number;
    /** Number of custom field values updated */
    updatedCount: number;
    /** Number of custom field values deleted */
    deletedCount: number;
    /** The document ID */
    documentId: number;
}

/**
 * One field whose stored value changed since the client loaded it — carried in the
 * 409 BizConcurrentUpdateConflict body (conflictingFields) so the UI can show WHAT
 * changed instead of a bare "someone else edited this".
 */
export interface CustomFieldConflictDto {
    customFieldDefinitionId: number;
    /** Set when the conflicting value is line-item-scoped */
    lineItemId?: number | null;
    fieldCode: string;
    fieldLabel: string;
    /** The value currently stored (what the other writer saved); null when it was deleted */
    currentValue?: string | null;
    /** When the stored value was written; null when the value was deleted */
    currentUpdatedAt?: string | null;
}

/**
 * Response for the document presence heartbeat (POST /documents/{id}/presence)
 */
export interface DocumentPresenceResponseDto {
    documentId: number;
    /** Distinct other users seen viewing this document within the presence TTL */
    otherViewerCount: number;
}

/**
 * Response for the document exportable-by-ID endpoint.
 * Contains the main document, all related documents, and relationship details.
 */
export interface DocumentExportableResponseDto {
    /** Main document (first) and related documents */
    documents: DocumentDetailDto[];
    /** Relationship records (main document as parent) */
    relationships: DocumentRelationDto[];
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get the value from a CustomFieldValueDto based on its field type
 * (For use with flattened DTO from DocumentDetailDto)
 * Returns the appropriate typed value based on the fieldType property
 */
export function getCustomFieldDtoDisplayValue(fieldValue: CustomFieldValueDto): string | number | boolean | null {
    const fieldType = fieldValue.fieldType;

    switch (fieldType) {
        case FieldTypeEnum.String:
            return fieldValue.stringValue ?? null;
        case FieldTypeEnum.Integer:
            return fieldValue.integerValue ?? null;
        case FieldTypeEnum.Decimal:
            return fieldValue.decimalValue ?? null;
        case FieldTypeEnum.Date:
            return fieldValue.dateValue ?? null;
        case FieldTypeEnum.Boolean:
            return fieldValue.booleanValue ?? null;
        default:
            // Try to find any non-null value
            return fieldValue.stringValue ?? fieldValue.decimalValue ?? fieldValue.integerValue ?? fieldValue.dateValue ?? fieldValue.booleanValue ?? null;
    }
}

/**
 * Check if a document status is a final state (stop polling)
 */
export function isDocumentFinalStatus(status: DocumentStatusEnum): boolean {
    return [
        DocumentStatusEnum.Parsed,
        DocumentStatusEnum.Queued,
        DocumentStatusEnum.Exported,
        DocumentStatusEnum.Rejected,
        DocumentStatusEnum.Error,
        DocumentStatusEnum.Deleted,
    ].includes(status);
}

/**
 * Check if a document status is a success state
 */
export function isDocumentSuccessStatus(status: DocumentStatusEnum): boolean {
    return [DocumentStatusEnum.Parsed, DocumentStatusEnum.Queued, DocumentStatusEnum.Exported].includes(status);
}

/**
 * Check if a document status is an error state
 */
export function isDocumentErrorStatus(status: DocumentStatusEnum): boolean {
    return [DocumentStatusEnum.Error, DocumentStatusEnum.Rejected].includes(status);
}

/**
 * Check if a document status DTO indicates an error state
 * Error state is: status is Error/Rejected OR errorCode is populated
 */
export function isDocumentStatusError(statusDto: DocumentStatusDto): boolean {
    return statusDto.status === DocumentStatusEnum.Error || statusDto.status === DocumentStatusEnum.Rejected || !!statusDto.errorCode;
}
